import { Anchor, Badge, Box, Collapse, Group, Paper, SegmentedControl, Skeleton, Stack, Text, Title } from '@mantine/core';
import type { MyLegalDocument } from '@motionsafe/ms-console-api/admin/v1/legal_pb.js';
import { IconScale } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { errorText, myLegalQuery } from '../../api/hooks';
import { LegalBlocks } from '../../components/legal-blocks';
import { EmptyState, PageHeader } from '../../components/page';
import { day } from '../../lib/format';
import { fromWire } from '../../lib/legal';

// The signed-in person's own legal documents (ARCHITECTURE.md §16). The
// documents are not public: this is where someone reads what they agreed to,
// with the version and the day, and what is in effect now when it has
// changed since. Opened from the account menu, whoever is signed in.
export const Route = createFileRoute('/_app/your-legal-documents')({ component: YourLegalDocuments });

function YourLegalDocuments() {
  const q = useQuery(myLegalQuery);
  const docs = q.data?.documents ?? [];
  const agreement = q.data?.agreement;
  return (
    <>
      <PageHeader
        title="Your legal documents"
        description="What you agreed to in order to use MotionSafe, the version, and the day you accepted it."
      />
      {agreement && (
        <Text size="sm" mb="lg">
          Your organization&rsquo;s agreement with MotionSafe, {agreement.title}, covers you, so you are not asked to accept these yourself.
        </Text>
      )}
      {q.isPending && (
        <Stack gap="lg" aria-hidden="true">
          {[0, 1].map((i) => <Paper key={i} p="lg"><Skeleton height={16} width="30%" mb="sm" /><Skeleton height={12} width="55%" /></Paper>)}
        </Stack>
      )}
      {q.isError && <Text size="sm" c="danger">Could not load your legal documents: {errorText(q.error)}.</Text>}
      {q.data && docs.length === 0 && (
        <Paper p={0}>
          <EmptyState icon={IconScale} title="Nothing to agree to yet" body="MotionSafe has no legal document in effect. When one is published, you are asked to accept it at your next sign-in, and it appears here." />
        </Paper>
      )}
      <Stack gap="lg">
        {docs.map((d) => <DocumentCard key={d.documentId} doc={d} covered={!!agreement} />)}
      </Stack>
    </>
  );
}

// One document: where the person stands on it in a sentence, then its text,
// folded until asked for. When the version they accepted is not the one in
// effect, either can be read.
function DocumentCard({ doc, covered }: { doc: MyLegalDocument; covered: boolean }) {
  const [reading, setReading] = useState(false);
  const differs = !!doc.accepted && !!doc.inEffect && doc.accepted.id !== doc.inEffect.id;
  const [which, setWhich] = useState<'accepted' | 'in_effect'>(doc.accepted ? 'accepted' : 'in_effect');
  const shown = which === 'accepted' ? doc.accepted ?? doc.inEffect : doc.inEffect ?? doc.accepted;
  const title = (doc.inEffect ?? doc.accepted)?.title ?? doc.key;
  const where = doc.acceptedOn === 'device' ? ' on a device' : doc.acceptedOn === 'console' ? ' on the console' : '';
  const standing = doc.accepted
    ? `You accepted version ${doc.accepted.version}${where}, ${day(doc.acceptedAt)}.`
    : covered ? 'Covered by your organization\'s agreement.' : 'Not accepted yet: you are asked at your next sign-in.';
  const after = doc.status === 'retired'
    ? ' MotionSafe has since retired it; nobody is asked to accept it now.'
    : differs && !doc.current && !covered
      ? ` Version ${doc.inEffect!.version} is in effect now, and you are asked to accept it at your next sign-in.`
      : differs
        ? ` Version ${doc.inEffect!.version} is in effect now; it corrected the text without asking again.`
        : '';
  const id = `legal-${doc.documentId}`;
  return (
    <Paper p="lg" component="section" aria-labelledby={`${id}-title`}>
      <Group gap="sm" align="center" wrap="nowrap" mb={4}>
        <Title order={2} fz="lg" id={`${id}-title`}>{title}</Title>
        {doc.status === 'retired' && <Badge color="gray">retired</Badge>}
      </Group>
      <Text size="sm" {...(doc.accepted || covered ? {} : { c: 'dimmed' })}>{standing}{after}</Text>
      {shown && (
        <Box mt="md">
          <Anchor component="button" type="button" size="sm" onClick={() => setReading((r) => !r)} aria-expanded={reading} aria-controls={`${id}-text`}>
            {reading ? 'Hide the text' : 'Read it'}
          </Anchor>
          <Collapse expanded={reading} transitionDuration={200} transitionTimingFunction="var(--ease-out)" id={`${id}-text`}>
            <Stack gap="md" mt="md">
              {differs && (
                <SegmentedControl
                  size="xs"
                  w="fit-content"
                  value={which}
                  onChange={(v) => setWhich(v as 'accepted' | 'in_effect')}
                  data={[
                    { value: 'accepted', label: `Version ${doc.accepted!.version}, which you accepted` },
                    { value: 'in_effect', label: `Version ${doc.inEffect!.version}, in effect` },
                  ]}
                />
              )}
              <Box className="prose">
                <LegalBlocks blocks={fromWire(shown.blocks)} size="sm" titleOrder={3} empty="This version has no text." />
              </Box>
            </Stack>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
}
